/* 글 속의 수식 조각. 생성 계약의 expression 과 condition 은 문항과 풀이에 제시된 이 조각들 중에서만 고른다. 조각은 $…$, $$…$$, \[…\], \(…\) 안의 글이고 글자 그대로 둔다 */

export interface Segment { index: number; text: string }

interface Scanned { text: string; before: string }

/** 수식 조각과, 그 조각 바로 앞의 글(앞 조각이 끝난 자리부터 이 조각이 시작하기 전까지) */
function scan(problem: string): Scanned[] {
  const out: Scanned[] = [];
  const text = String(problem ?? '');
  let i = 0;
  let last = 0;
  const push = (body: string, start: number) => { const t = body.trim(); if (t) out.push({ text: t, before: text.slice(last, start) }); };
  while (i < text.length) {
    if (text.startsWith('$$', i)) {
      const end = text.indexOf('$$', i + 2);
      if (end < 0) break;
      push(text.slice(i + 2, end), i); i = end + 2; last = i; continue;
    }
    if (text[i] === '$') {
      const end = text.indexOf('$', i + 1);
      if (end < 0) break;
      push(text.slice(i + 1, end), i); i = end + 1; last = i; continue;
    }
    if (text.startsWith('\\[', i)) {
      const end = text.indexOf('\\]', i + 2);
      if (end < 0) break;
      push(text.slice(i + 2, end), i); i = end + 2; last = i; continue;
    }
    if (text.startsWith('\\(', i)) {
      const end = text.indexOf('\\)', i + 2);
      if (end < 0) break;
      push(text.slice(i + 2, end), i); i = end + 2; last = i; continue;
    }
    i += 1;
  }
  return out;
}

export function splitSegments(problem: string): Segment[] {
  return scan(problem).map((s, index) => ({ index, text: s.text }));
}

/** 점을 가리키는 말. 이 말 바로 뒤에 오는 수식 조각의 대문자가 점 이름이다(점 A, 선분 AB, 삼각형 ABC, 교점을 R) */
const POINT_CUES = ['점', '선분', '삼각형', '사각형', '직선', '반직선'];
/** 점 이름이 나열될 때 이름 사이에 올 수 있는 글자 */
const LIST_GLUE = ',와과및 ';

function endsWithCue(before: string): boolean {
  const last = before.split('\n').join(' ').trim().split(' ').pop() ?? '';
  return last === '호' || POINT_CUES.some((cue) => last.includes(cue));
}

function onlyGlue(before: string): boolean {
  for (const ch of before.split('\n').join(' ')) if (!LIST_GLUE.includes(ch)) return false;
  return true;
}

const isUpper = (c: string) => c >= 'A' && c <= 'Z';
const isAlpha = (c: string) => isUpper(c) || (c >= 'a' && c <= 'z');

/** 수식 조각 안의 대문자 이름들. 역슬래시 명령(\overline, \angle)의 이름은 건너뛰고 연속한 대문자를 한 글자씩 낸다(AB 는 A 와 B) */
function capitals(segment: string): string[] {
  const out: string[] = [];
  let i = 0;
  while (i < segment.length) {
    if (segment[i] === '\\') { i += 1; while (i < segment.length && isAlpha(segment[i])) i += 1; continue; }
    if (isUpper(segment[i])) { out.push(segment[i]); i += 1; continue; }
    i += 1;
  }
  return out;
}

/** 문항이 이름을 붙인 점. 점을 가리키는 말 바로 뒤의 수식 조각(와 그 나열)에서만 뽑는다. 값이나 상수를 뜻하는 대문자($M$ 의 최솟값)는 뽑지 않는다 */
export function pointNamesIn(problem: string): string[] {
  const out: string[] = [];
  let carry: boolean = false;
  for (const seg of scan(problem)) {
    const named: boolean = endsWithCue(seg.before) || (carry && onlyGlue(seg.before));
    if (named) for (const ch of capitals(seg.text)) if (!out.includes(ch)) out.push(ch);
    carry = named;
  }
  return out;
}

const isHangul = (ch: string) => { const c = ch.charCodeAt(0); return (c >= 0xac00 && c <= 0xd7a3) || (c >= 0x3131 && c <= 0x318e); };
const MATH_MARK = ['=', '<', '>', '≤', '≥', '(', '^', '/'];

/** 식이 $ 로 감싸이지 않은 평문에서 식 토막을 고른다: 공백으로 나눈 토막에서 앞뒤 한글(조사)과 문장 부호를 떼고, 관계 기호나 괄호가 있고 계산 패키지가 읽는 것만.
 * $ 조각이 하나도 없는 평문 문항도 제시된 식을 가지게 하기 위한 것이다 */
export function plainFormulas(text: string, reads: (s: string) => boolean): string[] {
  const out: string[] = [];
  const words: string[] = []; let cur = '';
  for (const ch of String(text ?? '')) { if (ch === ' ' || ch === '\n' || ch === '\t' || ch === '\r') { if (cur) words.push(cur); cur = ''; } else cur += ch; }
  if (cur) words.push(cur);
  for (const raw of words) {
    let t = raw;
    while (t.length && (isHangul(t[0]) || '(,.;:「」'.includes(t[0]) && t[0] !== '(')) t = t.slice(1);
    while (t.length && (isHangul(t[t.length - 1]) || ',.;:?!」'.includes(t[t.length - 1]))) t = t.slice(0, -1);
    if (t.length < 3 || !MATH_MARK.some((m) => t.includes(m))) continue;
    if (!out.includes(t) && reads(t)) out.push(t);
  }
  return out;
}

/** 여러 글의 수식 조각을 합쳐 중복 없이. 생성 계약에 주는 「제시된 식」. $ 조각이 하나도 없는 글은 평문에서 고른다 */
export function givenFormulas(...texts: string[]): string[];
export function givenFormulas(reads: (s: string) => boolean, ...texts: string[]): string[];
export function givenFormulas(first: string | ((s: string) => boolean), ...texts: string[]): string[] {
  const reads = typeof first === 'function' ? first : () => true;
  const all = typeof first === 'function' ? texts : [first, ...texts];
  const out: string[] = [];
  for (const t of all) {
    const segments = splitSegments(t).map((s) => s.text);
    for (const s of segments.length ? segments : plainFormulas(t, reads)) if (!out.includes(s)) out.push(s);
  }
  return out;
}

/** \( \) 와 \[ \] 구분자를 $ $ 와 $$ $$ 로 바꾼다. 화면의 마크다운(remark-math)은 $ 만 안다 */
export function normalizeDelimiters(text: string): string {
  let out = '';
  let i = 0;
  const t = String(text ?? '');
  while (i < t.length) {
    if (t.startsWith('\\(', i)) { const end = t.indexOf('\\)', i + 2); if (end < 0) break; out += `$${t.slice(i + 2, end).trim()}$`; i = end + 2; continue; }
    if (t.startsWith('\\[', i)) { const end = t.indexOf('\\]', i + 2); if (end < 0) break; out += `$$${t.slice(i + 2, end).trim()}$$`; i = end + 2; continue; }
    out += t[i]; i += 1;
  }
  return out + t.slice(i);
}
